import { useState } from "react";
import { useGqlMutation, useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  blogAuthorListQuery,
  createBlogAuthorMutation,
  deleteBlogAuthorMutation,
  updateBlogAuthorMutation,
  BlogAuthorListItem,
} from "@/app/Blogs/GraphQL/blogAuthorQueries";
import { RecordCreateCard } from "@/lib/Components/SideBarList/Cards/RecordCreateCard";
import { BlogAuthorForm } from "@/app/Blogs/Forms/BlogAuthorForm";
import { toFormikValidate } from "@/lib/Zod/zod-formik-adapter";
import { blogAuthorValidation } from "@/app/Blogs/Validation/blogValidation";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/catalyst/button";
import { Dialog, DialogTitle } from "@/components/catalyst/dialog";
import { CreateBlogAuthorInput, UpdateBlogAuthorInput } from "@/gql/graphql";

export function BlogAuthorsScreen() {
  const [editing, setEditing] = useState<BlogAuthorListItem | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data } = useGqlQuery(blogAuthorListQuery, {});
  const { mutateAsync: createAuthor } = useGqlMutation(
    createBlogAuthorMutation,
  );
  const { mutateAsync: updateAuthor } = useGqlMutation(
    updateBlogAuthorMutation,
  );
  const { mutateAsync: deleteAuthor } = useGqlMutation(
    deleteBlogAuthorMutation,
  );

  const authors = data?.blogAuthors ?? [];

  const invalidate = () =>
    queryClient.invalidateQueries({
      predicate: (q) => q.queryKey.includes("BlogAuthorList"),
    });

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold">Blog authors</h1>
        <Button onClick={() => setCreateOpen(true)}>New author</Button>
      </div>

      <Dialog open={createOpen} onClose={setCreateOpen}>
        <DialogTitle>New author</DialogTitle>
        <RecordCreateCard<CreateBlogAuthorInput>
          hideTitle
          validate={toFormikValidate(blogAuthorValidation)}
          onSubmit={async (values) => {
            await createAuthor({ input: values });
            await invalidate();
            setCreateOpen(false);
          }}
          initialValues={{
            name: "",
            role: null,
            bio: null,
          }}
        >
          <BlogAuthorForm />
        </RecordCreateCard>
      </Dialog>

      <ul className="divide-y divide-zinc-200 rounded-md border border-zinc-200 bg-white">
        {authors.map((author) => (
          <li
            key={author.id}
            className="flex items-center justify-between px-4 py-3"
          >
            <div>
              <div className="font-medium">{author.name}</div>
              <div className="text-xs text-zinc-500">{author.role}</div>
            </div>
            <div className="flex gap-2">
              <Button outline onClick={() => setEditing(author)}>
                Edit
              </Button>
              <Button
                outline
                onClick={async () => {
                  if (!confirm("Delete this author?")) return;
                  await deleteAuthor({ id: author.id });
                  await invalidate();
                }}
              >
                Delete
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <Dialog open={editing !== null} onClose={(o) => !o && setEditing(null)}>
        <DialogTitle>Edit author</DialogTitle>
        {editing ? (
          <RecordCreateCard<UpdateBlogAuthorInput>
            hideTitle
            validate={
              toFormikValidate(blogAuthorValidation) as (
                values: UpdateBlogAuthorInput,
              ) => Promise<Record<string, string> | undefined>
            }
            onSubmit={async (values) => {
              await updateAuthor({ id: editing.id, input: values });
              await invalidate();
              setEditing(null);
            }}
            initialValues={{
              name: editing.name,
              role: editing.role,
              bio: editing.bio,
            }}
          >
            <BlogAuthorForm />
          </RecordCreateCard>
        ) : null}
      </Dialog>
    </div>
  );
}
